import { useQueryClient } from "@tanstack/react-query";
import { KeyRound, Loader2 } from "lucide-react";
import { useState } from "react";
import { FormattedMessage } from "react-intl";
import { useLocation, useNavigate } from "react-router-dom";
import { useCompleteNewPassword } from "~/api/authentication/authentication";
import {
  getGetCurrentUserQueryKey,
  useGetPasswordRules,
} from "~/api/users/users";
import SmartPasswordInput from "~/components/smart-password-input";
import { storeAccessToken, storeRefreshToken, storeUsername } from "~/helpers";
import { useThemedOnboardingImage } from "~/hooks/useThemedOnboardingImage";
import { useAuthStepImage } from "../hooks/useAuthStepImage";

export const ChangePasswordForm = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const queryClient = useQueryClient();
  const { session, username } = location.state || {};
  const [isNavigating, setIsNavigating] = useState(false);

  const passwordBg = useThemedOnboardingImage("password");
  useAuthStepImage(passwordBg);

  const {
    mutate: completeNewPassword,
    isPending,
    isError,
  } = useCompleteNewPassword();

  const { data: passwordRules, isLoading: isLoadingPasswordRules } =
    useGetPasswordRules();

  const onSavePasswordClick = async (password: string) => {
    completeNewPassword(
      { data: { newPassword: password, session, username } },
      {
        onSuccess: async (data) => {
          if (data.AccessToken && data.RefreshToken) {
            storeAccessToken(data.AccessToken);
            storeRefreshToken(data.RefreshToken);
            storeUsername(username);
            setIsNavigating(true);

            try {
              // Invalidate and refetch user data before navigating
              // This ensures AppLayout has the user data in cache and won't redirect to login
              await queryClient.invalidateQueries({
                queryKey: getGetCurrentUserQueryKey(),
              });
              await queryClient.refetchQueries({
                queryKey: getGetCurrentUserQueryKey(),
              });
            } catch {
              // If refetch fails, still navigate - AppLayout will handle the auth check
            }

            navigate("/");
          }
        },
      }
    );
  };

  if (isNavigating) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[400px] gap-4">
        <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
        <p className="text-sm text-muted-foreground">
          <FormattedMessage
            id="changePasswordForm.signingIn"
            defaultMessage="Signing in..."
          />
        </p>
      </div>
    );
  }

  return (
    <div className="flex justify-center items-start">
      {isLoadingPasswordRules ? (
        <div className="flex flex-col items-center gap-4">
          <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
        </div>
      ) : (
        <div className="flex flex-col gap-4 sm:gap-6 max-w-[560px] w-full">
          <div className="flex flex-col gap-2 sm:gap-4">
            <KeyRound className="h-[32px] w-[32px] text-[#71717A]" />
            <h3 className="text-2xl font-semibold text-base-foreground pt-2 sm:pt-[16px]">
              <FormattedMessage
                id="changePasswordForm.title"
                defaultMessage="Create New Password"
              />
            </h3>
            <span className="text-sm text-muted-foreground">
              <FormattedMessage
                id="changePasswordForm.subtitle"
                defaultMessage="Change your temporary password to a personal and secure one."
              />
            </span>
          </div>
          <SmartPasswordInput
            onPasswordSubmit={onSavePasswordClick}
            activeRequirements={passwordRules?.rules || []}
            isPending={isPending}
          />
          {isError && (
            <p className="mt-4 text-red-600 text-sm text-center">
              <FormattedMessage
                id="changePasswordForm.error"
                defaultMessage="Failed to change password"
              />
            </p>
          )}
        </div>
      )}
    </div>
  );
};
